import { store } from "@app/store";
import { API_URL } from "@constants";

export type ErrorResponse = {
  details: string;
  error: string;
  code?: string;
};

export class FetchClientError extends Error {
  status: number;
  data?: string;

  constructor(message: string, status: number, data?: string) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export class JSONFetchClientError extends Error {
  data: ErrorResponse;

  constructor(data: ErrorResponse) {
    super(data.error);
    this.data = data;
  }
}

export type FetchClientOptions = {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  headers?: Record<string, string>;
};

export const fetchClient = async <T = unknown>({
  url,
  method = "GET",
  data,
  headers = {},
}: FetchClientOptions): Promise<T> => {
  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  };

  const response = await fetch(`${API_URL}/${url}`, options);

  if (!response.ok) {
    const data = await response.json();
    const message = data.details ?? JSON.stringify(data);

    throw new FetchClientError(
      `Request failed with status ${response.status}`,
      response.status,
      message
    );
  }

  return response.json() as Promise<T>;
};

export const postFiles = async <T = unknown>({
  url,
  method = "POST",
  data,
  headers = {},
}: FetchClientOptions): Promise<T> => {
  const { accessToken } = store.getState().auth;

  const options: RequestInit = {
    method,
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: data || undefined,
  };
  const response = await fetch(`${API_URL}/${url}`, options);

  if (!response.ok) {
    const data = await response.text();

    throw new FetchClientError(
      `Request failed with status ${response.status}`,
      response.status,
      data
    );
  }

  return response.json() as Promise<T>;
};

export const fetchCSV = async ({
  url,
  method = "GET",
  data,
  headers = {},
}: FetchClientOptions) => {
  const { accessToken } = store.getState().auth;

  const options: RequestInit = {
    method,
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: data || undefined,
  };
  const response = await fetch(`${API_URL}/${url}`, options);

  if (!response.ok) {
    const data = await response.text();

    throw new FetchClientError(
      `Request failed with status ${response.status}`,
      response.status,
      data
    );
  }
  // Get the blob from the response
  const blob = await response.blob();

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = `orders_export_${new Date().toISOString().split("T")[0]}.csv`; // Fallback filename
  if (contentDisposition) {
    const matches = contentDisposition.match(/filename="([^"]+)"/);
    if (matches && matches[1]) {
      filename = matches[1];
    }
  }

  // Create a temporary URL for the blob
  const urlObject = window.URL.createObjectURL(blob);

  // Create a link element to trigger download
  const link = document.createElement("a");
  link.href = urlObject;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  window.URL.revokeObjectURL(urlObject);
};
